import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  Stack,
  Typography,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  TextField
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Download } from '@mui/icons-material';
import moment, { Moment } from 'moment';
import { useSelector } from 'react-redux';
import Papa from 'papaparse';
import DatePicker from '../../../../components/DatePicker';
import axios from '../../../../resources/axios';
import { selectCities } from '../../../../redux/slices/citiesSlice';
import { selectAdmins } from '../../../../redux/slices/adminsSlice';
import { selectAuth } from '../../../../redux/slices/authSlice';
import { TripCSVData, UserActivityCSVData, TripData, UserActivity, AppOpenData } from './types';
import GeneralStatistics from './GeneralStatistics';
import TripMapView from './TripMapView';
import PlaybackMapView from './PlaybackMapView';
import RentHeatMapSection from '../../../../components/sections/RentHeatMapSection';

const CITY_SHORT_MAP: Record<string, string> = {
  '5ff99e7d863d42769b05c486': 'IF',
  '60d59e08bd31e836ae204610': 'VN',
  '60058dc791cd964825147a6a': 'ZT',
  '63f7b73cfcb1a10e71e62078': 'BT',
  '63fc94c0fcb1a10e71e620b7': 'KL',
  '66280975a8831e51b380e3cd': 'CHR',
  '66a2533d5f572298529446b8': 'VSK',
  '68721a559e0fad9ff9ad52a9': 'KAL'
};

const getCityShort = (cityId: string): string => {
  return CITY_SHORT_MAP[cityId] || cityId;
};

const parseLocation = (location: string): [number, number] | null => {
  if (!location) return null;
  const parts = location.split(',').map(s => parseFloat(s.trim()));
  if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
    return [parts[0], parts[1]];
  }
  return null;
};

interface AIDemandAnalyticsTabProps {
  mapCenter?: { lat: number; lng: number };
  currentCityId?: string;
}

const AIDemandAnalyticsTab: React.FC<AIDemandAnalyticsTabProps> = ({ mapCenter, currentCityId }) => {
  const theme = useTheme();
  const cities = useSelector(selectCities);
  const adminInfo = useSelector(selectAdmins);
  const auth = useSelector(selectAuth);

  const [selectedDate, setSelectedDate] = useState<Moment | null>(moment().subtract(1, 'day'));
  const [timeFrom, setTimeFrom] = useState<string>('00:00');
  const [timeTo, setTimeTo] = useState<string>('23:59');
  const [loadingTrips, setLoadingTrips] = useState(false);
  const [loadingActivity, setLoadingActivity] = useState(false);
  const [loadingAppOpens, setLoadingAppOpens] = useState(false);
  const [currentSubTab, setCurrentSubTab] = useState(0);
  const [error, setError] = useState<string>('');
  const [dataLoaded, setDataLoaded] = useState(false);
  
  const [tripsData, setTripsData] = useState<TripData[]>([]);
  const [userActivityData, setUserActivityData] = useState<UserActivity[]>([]);
  const [appOpenData, setAppOpenData] = useState<AppOpenData | null>(null);

  const availableCities = useMemo(() => {
    if (auth.info?.type === 'superAdmin') {
      return cities.items;
    }
    if (adminInfo.info?.cities) {
      return adminInfo.info.cities;
    }
    return [];
  }, [auth.info?.type, adminInfo.info?.cities, cities.items]);

  const currentCity = useMemo(() => {
    return availableCities.find(item => item._id === adminInfo.currentCity);
  }, [adminInfo.currentCity, availableCities]);

  // Автоматично використовуємо обране місто з бокового меню
  const selectedCityShort = useMemo(() => {
    if (currentCity) {
      return getCityShort(currentCity._id);
    }
    return availableCities.length > 0 ? getCityShort(availableCities[0]._id) : '';
  }, [currentCity, availableCities]);

  const selectedCityObject = useMemo(() => {
    return availableCities.find(city => getCityShort(city._id) === selectedCityShort);
  }, [availableCities, selectedCityShort]);

  const fetchTripsData = useCallback(async () => {
    if (!selectedDate || !selectedCityShort) return;

    setLoadingTrips(true);
    try {
      const startOfDay = selectedDate.clone().startOf('day');
      const endOfDay = selectedDate.clone().endOf('day');
      const dateFromStr = startOfDay.format('YYYY-MM-DD HH:mm');
      const dateToStr = endOfDay.format('YYYY-MM-DD HH:mm');
      const url = `/statistics/contracts_csv_for_ai_analyze?dateFrom=${dateFromStr}&dateTo=${dateToStr}&cityShort=${selectedCityShort}`;
      
      const response = await axios.get(url, {
        responseType: 'text'
      });

      if (!response.data || response.data.trim() === '') {
        console.warn('Trips data is empty for the selected period');
        setTripsData([]);
        setDataLoaded(true);
        return;
      }

      const parsed = Papa.parse<TripCSVData>(response.data, { 
        header: true,
        skipEmptyLines: true
      });

      const processedData: TripData[] = parsed.data
        .filter(row => {
          const startLoc = parseLocation(row.startLocation);
          const endLoc = parseLocation(row.endLocation);
          return row.userId && startLoc && endLoc && 
                 startLoc[0] !== 0 && startLoc[1] !== 0 &&
                 endLoc[0] !== 0 && endLoc[1] !== 0;
        })
        .map(row => {
          const startLoc = parseLocation(row.startLocation)!;
          const endLoc = parseLocation(row.endLocation)!;
          const availableLoc = parseLocation(row.availableSetLocation);

          return {
            userId: row.userId,
            scooterId: row.scooterId,
            startTime: new Date(row.startTime),
            endTime: new Date(row.endTime),
            startLocation: startLoc,
            endLocation: endLoc,
            distance: parseFloat(row.distance) || 0,
            duration: parseFloat(row.duration) || 0,
            cost: parseFloat(row.cost) || 0,
            batteryStart: parseFloat(row.batteryStart) || 0,
            batteryEnd: parseFloat(row.batteryEnd) || 0,
            availableSetTime: row.availableSetTime ? new Date(row.availableSetTime) : null,
            availableSetLocation: availableLoc
          };
        });

      setTripsData(processedData);
      setDataLoaded(true);
    } catch (error: any) {
      console.error('Error fetching trips data:', error);
      setError(`Помилка завантаження поїздок: ${error.response?.data?.message || error.message || 'Невідома помилка'}`);
      setTripsData([]);
      setDataLoaded(true);
    } finally {
      setLoadingTrips(false);
    }
  }, [selectedDate, selectedCityShort]);

  const fetchUserActivityData = useCallback(async () => {
    if (!selectedDate || !selectedCityShort) return;

    setLoadingActivity(true);
    try {
      const startOfDay = selectedDate.clone().startOf('day');
      const endOfDay = selectedDate.clone().endOf('day');
      const dateFromStr = startOfDay.format('YYYY-MM-DD HH:mm');
      const dateToStr = endOfDay.format('YYYY-MM-DD HH:mm');
      const url = `/statistics/user_activity_csv?dateFrom=${dateFromStr}&dateTo=${dateToStr}&cityShort=${selectedCityShort}`;
      
      const response = await axios.get(url, {
        responseType: 'text'
      });

      if (!response.data || response.data.trim() === '') {
        console.warn('User activity data is empty for the selected period');
        setUserActivityData([]);
        return;
      }

      const parsed = Papa.parse<UserActivityCSVData>(response.data, { 
        header: true,
        skipEmptyLines: true
      });

      const processedData: UserActivity[] = parsed.data
        .filter(row => {
          const loc = parseLocation(row.location);
          return row.userId && loc && loc[0] !== 0 && loc[1] !== 0;
        })
        .map(row => {
          const loc = parseLocation(row.location)!;

          return {
            userId: row.userId,
            city: row.city,
            timestamp: new Date(row.timestamp),
            location: loc,
            nearbyScooters: parseInt(row.nearbyScooters) || 0,
            rentedAfter: row.rentedAfter === 'true' || row.rentedAfter === '1'
          };
        });

      setUserActivityData(processedData);
    } catch (error: any) {
      console.error('Error fetching user activity data:', error);
      setUserActivityData([]);
    } finally {
      setLoadingActivity(false);
    }
  }, [selectedDate, selectedCityShort]);

  const fetchAppOpenData = useCallback(async () => {
    if (!selectedDate || !selectedCityShort) return;

    setLoadingAppOpens(true);
    try {
      const dateStart = selectedDate.clone().startOf('day');
      const dateEnd = selectedDate.clone().endOf('day');
      const url = `/statistics/scooter_list_request_stats?dateFrom=${dateStart.toISOString()}&dateTo=${dateEnd.toISOString()}&cityShort=${selectedCityShort}`;
      
      const response = await axios.get(url);

      if (response.data && response.data.data) {
        setAppOpenData(response.data.data);
      } else {
        console.warn('App open data is empty for the selected period');
        setAppOpenData(null);
      }
    } catch (error: any) {
      console.error('Error fetching app open data:', error);
      setAppOpenData(null);
    } finally {
      setLoadingAppOpens(false);
    }
  }, [selectedDate, selectedCityShort]);

  const handleSubTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setCurrentSubTab(newValue);
  };

  const handleLoadData = () => {
    setDataLoaded(false);
    fetchTripsData();
    fetchUserActivityData();
    fetchAppOpenData();
  };

  // Check if time range is valid
  const isTimeRangeValid = useMemo(() => {
    if (!timeFrom || !timeTo) return true;
    const [fromHours, fromMinutes] = timeFrom.split(':').map(Number);
    const [toHours, toMinutes] = timeTo.split(':').map(Number);
    const fromTime = fromHours * 60 + fromMinutes;
    const toTime = toHours * 60 + toMinutes;
    return fromTime <= toTime;
  }, [timeFrom, timeTo]);

  // Filter data by selected time range
  const filteredTripsData = useMemo(() => {
    if (!dataLoaded || !timeFrom || !timeTo) return tripsData;
    
    const [fromHours, fromMinutes] = timeFrom.split(':').map(Number);
    const [toHours, toMinutes] = timeTo.split(':').map(Number);
    
    return tripsData.filter(trip => {
      const tripDate = new Date(trip.startTime);
      const tripHours = tripDate.getHours();
      const tripMinutes = tripDate.getMinutes();
      const tripTime = tripHours * 60 + tripMinutes;
      const fromTime = fromHours * 60 + fromMinutes;
      const toTime = toHours * 60 + toMinutes;
      
      return tripTime >= fromTime && tripTime <= toTime;
    });
  }, [tripsData, timeFrom, timeTo, dataLoaded]);

  const filteredUserActivityData = useMemo(() => {
    if (!dataLoaded || !timeFrom || !timeTo) return userActivityData;
    
    const [fromHours, fromMinutes] = timeFrom.split(':').map(Number);
    const [toHours, toMinutes] = timeTo.split(':').map(Number);
    
    return userActivityData.filter(activity => {
      const activityDate = new Date(activity.timestamp);
      const activityHours = activityDate.getHours();
      const activityMinutes = activityDate.getMinutes();
      const activityTime = activityHours * 60 + activityMinutes;
      const fromTime = fromHours * 60 + fromMinutes;
      const toTime = toHours * 60 + toMinutes;
      
      return activityTime >= fromTime && activityTime <= toTime;
    });
  }, [userActivityData, timeFrom, timeTo, dataLoaded]);

  const filteredAppOpenData = useMemo(() => {
    if (!dataLoaded || !timeFrom || !timeTo || !appOpenData) return appOpenData;
    
    const [fromHours, fromMinutes] = timeFrom.split(':').map(Number);
    const [toHours, toMinutes] = timeTo.split(':').map(Number);
    
    const filteredCoords = (appOpenData.coords || []).filter((coord) => {
      const coordDate = new Date(coord.createdAt);
      const coordHours = coordDate.getHours();
      const coordMinutes = coordDate.getMinutes();
      const coordTime = coordHours * 60 + coordMinutes;
      const fromTime = fromHours * 60 + fromMinutes;
      const toTime = toHours * 60 + toMinutes;
      
      return coordTime >= fromTime && coordTime <= toTime;
    });
    
    return {
      ...appOpenData,
      coords: filteredCoords,
      totalOpens: filteredCoords.length
    };
  }, [appOpenData, timeFrom, timeTo, dataLoaded]);

  return (
    <Box>
      <Paper
        sx={{
          padding: '20px',
          border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none',
          borderRadius: 2,
          mb: 3
        }}
      >
        <Typography
          variant='h6'
          sx={{
            fontWeight: 600,
            fontSize: 18,
            color: theme.palette.mode === 'light' ? '#424242' : '#FFFFFF',
            mb: 1
          }}
        >
          AI Аналітика Попиту
        </Typography>

        <Typography
          sx={{
            fontSize: 14,
            color: theme.palette.mode === 'light' ? '#757575' : '#9E9E9E',
            mb: 3
          }}
        >
          📅 Оберіть дату та натисніть кнопку Завантажити для завантаження даних за весь день. Місто: {currentCity?.cityName || 'Не обрано'}
        </Typography>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <Box sx={{ minWidth: 200 }}>
            <DatePicker
              label="Дата"
              value={selectedDate}
              onChange={(newDate) => setSelectedDate(newDate)}
            />
          </Box>

          <Button
            variant="contained"
            startIcon={(loadingTrips || loadingAppOpens || loadingActivity) ? <CircularProgress size={20} color="inherit" /> : <Download />}
            onClick={handleLoadData}
            disabled={loadingTrips || loadingAppOpens || loadingActivity || !selectedDate || !selectedCityShort}
            sx={{
              backgroundColor: '#5073F8',
              '&:hover': { backgroundColor: '#3d5dd6' }
            }}
          >
            Завантажити
          </Button>
        </Stack>

        {dataLoaded && (
          <>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 2 }}>
              <TextField
                label="Час від"
                type="time"
                value={timeFrom}
                onChange={(e) => setTimeFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
                error={!isTimeRangeValid}
                sx={{ minWidth: 150 }}
              />
              <TextField
                label="Час до"
                type="time"
                value={timeTo}
                onChange={(e) => setTimeTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
                error={!isTimeRangeValid}
                sx={{ minWidth: 150 }}
              />
              <Typography
                sx={{
                  fontSize: 14,
                  color: theme.palette.mode === 'light' ? '#757575' : '#9E9E9E',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                ⏰ Виберіть часовий діапазон для фільтрації даних
              </Typography>
            </Stack>

            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  fontSize: 13,
                  color: theme.palette.mode === 'light' ? '#757575' : '#9E9E9E',
                  mb: 1
                }}
              >
                Швидкий вибір:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {Array.from({ length: 18 }, (_, i) => {
                  const startHour = 6 + i;
                  const endHour = startHour + 1;
                  const fromTime = `${startHour.toString().padStart(2, '0')}:00`;
                  const toTime = endHour === 24 ? '23:59' : `${endHour.toString().padStart(2, '0')}:00`;
                  const label = endHour === 24 ? `${startHour}:00-23:59` : `${startHour}:00-${endHour}:00`;
                  
                  const isActive = timeFrom === fromTime && timeTo === toTime;
                  
                  return (
                    <Button
                      key={i}
                      size="small"
                      variant={isActive ? 'contained' : 'outlined'}
                      onClick={() => {
                        setTimeFrom(fromTime);
                        setTimeTo(toTime);
                      }}
                      sx={{
                        minWidth: 'auto',
                        px: 1.5,
                        py: 0.5,
                        fontSize: 12,
                        textTransform: 'none',
                        backgroundColor: isActive ? '#5073F8' : 'transparent',
                        borderColor: theme.palette.mode === 'light' ? '#E0E0E0' : '#424242',
                        color: isActive ? '#FFF' : (theme.palette.mode === 'light' ? '#616161' : '#B0B0B0'),
                        '&:hover': {
                          backgroundColor: isActive ? '#3d5dd6' : (theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.04)' : 'rgba(255, 255, 255, 0.08)'),
                          borderColor: isActive ? '#3d5dd6' : '#5073F8'
                        }
                      }}
                    >
                      {label}
                    </Button>
                  );
                })}
              </Box>
            </Box>

            {!isTimeRangeValid && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                ⚠️ Час "від" не може бути пізніше ніж час "до". Будь ласка, виберіть коректний діапазон.
              </Alert>
            )}
          </>
        )}

        {error && (
          <Alert 
            severity="error" 
            onClose={() => setError('')}
            sx={{ mt: 2 }}
          >
            {error}
          </Alert>
        )}
      </Paper>

      <Paper
        sx={{
          border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none',
          borderRadius: 2
        }}
      >
        <Tabs
          value={currentSubTab}
          onChange={handleSubTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)',
            px: 2,
            '& .MuiTab-root': {
              minHeight: 48,
              textTransform: 'none',
              fontSize: '0.875rem',
              fontWeight: 500,
              color: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0',
              '&.Mui-selected': {
                color: theme.palette.mode === 'light' ? '#424242' : '#5073F8',
              },
            },
            '& .MuiTabs-indicator': {
              backgroundColor: theme.palette.mode === 'light' ? '#424242' : '#5073F8',
              height: 2,
            },
          }}
        >
          <Tab label="Загальна статистика" />
          <Tab label="Карта поїздок і відкриттів" />
          <Tab label="Playback карти та відкритя застосунку" />
          <Tab label="Мапа оренди" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {currentSubTab === 0 && (
            <GeneralStatistics 
              tripsData={filteredTripsData} 
              userActivityData={filteredUserActivityData}
              appOpenData={filteredAppOpenData}
              loading={loadingTrips || loadingActivity || loadingAppOpens}
            />
          )}
          {currentSubTab === 1 && (
            <TripMapView 
              tripsData={filteredTripsData} 
              userActivityData={filteredUserActivityData}
              appOpenData={filteredAppOpenData}
              selectedCity={selectedCityObject}
            />
          )}
          {currentSubTab === 2 && (
            <PlaybackMapView 
              appOpenData={appOpenData}
              selectedCity={selectedCityObject}
              selectedDate={selectedDate}
            />
          )}
          {currentSubTab === 3 && mapCenter && currentCityId && (
            <Paper 
              sx={{ 
                border: theme.palette.mode === 'light' ? '2px solid #FFFFFF' : 'none', 
                borderRadius: 2,
                padding: '20px'
              }}
            >
              <RentHeatMapSection mapCenter={mapCenter} currentCityId={currentCityId} />
            </Paper>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default AIDemandAnalyticsTab;
